import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, ArrowUp, ArrowDown, Pin, PinOff } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { getAuthHeaders } from '@/hooks/useAuth';

// Storefront order on /marketplace. The directory sorts by product count, which
// buries a brand-new partner store behind everyone with a back catalogue. Pin the
// first few by hand; everything unpinned keeps the most-active-first order below.

type Row = { slug: string; displayName: string; imageUrl: string | null; rank: number | null; productCount: number };

export function MarketplaceOrderPanel() {
  const { toast } = useToast();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const load = () => {
    setLoading(true);
    fetch('/api/admin/marketplace/order', { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => { setRows(d.creators || []); setDirty(false); })
      .catch(e => toast({ title: 'Could not load storefronts', description: e.message, variant: 'destructive' }))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const pinned = rows.filter(r => r.rank != null);
  const rest = rows.filter(r => r.rank == null);

  // Renumber after every change so rank is always 1..n with no gaps.
  const commit = (nextPinned: Row[], nextRest: Row[]) => {
    setRows([...nextPinned.map((r, i) => ({ ...r, rank: i + 1 })), ...nextRest.map(r => ({ ...r, rank: null }))]);
    setDirty(true);
  };
  const move = (i: number, dir: -1 | 1) => {
    const next = pinned.slice();
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    commit(next, rest);
  };
  const pin = (slug: string) => {
    const row = rest.find(r => r.slug === slug);
    if (!row) return;
    commit([...pinned, row], rest.filter(r => r.slug !== slug));
  };
  const unpin = (slug: string) => {
    const row = pinned.find(r => r.slug === slug);
    if (!row) return;
    const nextRest = [...rest, row].sort((a, b) => b.productCount - a.productCount || a.displayName.localeCompare(b.displayName));
    commit(pinned.filter(r => r.slug !== slug), nextRest);
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch('/api/admin/marketplace/order', {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pinned: pinned.map(r => r.slug) }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Save failed');
      toast({ title: pinned.length ? `${pinned.length} storefronts pinned` : 'Pins cleared' });
      setDirty(false);
    } catch (e: any) {
      toast({ title: 'Could not save the order', description: e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const Avatar = ({ r }: { r: Row }) => (
    <span className="inline-flex items-center justify-center overflow-hidden rounded-full bg-muted" style={{ width: 28, height: 28, flexShrink: 0 }}>
      {r.imageUrl ? <img src={r.imageUrl.startsWith('http') ? r.imageUrl : `/api/s3/${r.imageUrl}`} alt="" className="w-full h-full object-cover" /> : null}
    </span>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>Marketplace order</CardTitle>
        <CardDescription>
          Pinned storefronts lead the directory in this order. Everything else follows, most products first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Pinned to the top</div>
              {pinned.length === 0 && <div className="text-sm text-muted-foreground">Nothing pinned. The directory is ordered by product count.</div>}
              {pinned.map((r, i) => (
                <div key={r.slug} className="flex items-center gap-3 rounded-md border px-3 py-2">
                  <Badge variant="secondary" className="tabular-nums">{i + 1}</Badge>
                  <Avatar r={r} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{r.displayName}</div>
                    <div className="text-xs text-muted-foreground">{r.productCount} listed</div>
                  </div>
                  <Button size="icon" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up"><ArrowUp className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => move(i, 1)} disabled={i === pinned.length - 1} aria-label="Move down"><ArrowDown className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => unpin(r.slug)} aria-label="Unpin"><PinOff className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <div className="text-xs font-medium text-muted-foreground">Everyone else, most products first</div>
              {rest.map(r => (
                <div key={r.slug} className="flex items-center gap-3 rounded-md border border-dashed px-3 py-2">
                  <Avatar r={r} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{r.displayName}</div>
                    <div className="text-xs text-muted-foreground">{r.productCount} listed</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => pin(r.slug)}><Pin className="w-3.5 h-3.5 mr-1.5" /> Pin</Button>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <Button onClick={save} disabled={!dirty || saving}>{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save order</Button>
              {dirty && <Button variant="ghost" onClick={load}>Discard</Button>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
